import { useEffect, useRef, type RefObject } from 'react';

/**
 * A11Y-001 wave 1 — shared focus trap for dialog / drawer surfaces.
 *
 * 53 separate places in this codebase render `role="dialog"` with no
 * single primitive wrapping them — every drawer is its own bespoke
 * component (see CoportalAgentDrawer, LogEditModal, AdminUserDrawer,
 * etc.). Until a shared `<Drawer>` primitive lands, this hook is the
 * one piece of glue we can drop in everywhere without a refactor.
 *
 * Contract:
 *
 *   - On `open` flip from false → true:
 *       1. Remember `document.activeElement` (the trigger).
 *       2. Move focus to `options.initialFocusRef?.current`, else the
 *          first focusable inside `containerRef.current`, else the
 *          container itself with `tabIndex=-1`.
 *   - While open, intercept `Tab` / `Shift+Tab` and wrap focus between
 *     the first and last focusable descendant of the container — this
 *     is what stops keyboard users from tabbing out into the page
 *     underneath the modal.
 *   - If `onEscape` is supplied, Escape calls it (caller decides whether
 *     to close — we don't force the contract).
 *   - On `open` flip from true → false, restore focus to the remembered
 *     trigger element (if it's still in the document).
 *
 * `prefers-reduced-motion` is respected by virtue of doing nothing
 * animated — we never scroll/animate the focus shift, we just call
 * `.focus({ preventScroll: false })`. Browsers handle the
 * `scroll-behavior: auto` vs `smooth` decision at the CSS level; this
 * hook does not opt into smooth scrolling.
 *
 * Limitations (intentional, kept simple):
 *
 *   - We treat every focusable element rendered inside the container at
 *     keystroke time as in-trap — no MutationObserver. If a dialog
 *     reveals new fields mid-flight (e.g. an inline confirmation), the
 *     next Tab simply picks them up.
 *   - We do not pierce shadow-DOM (none of the touched drawers use
 *     shadow-DOM).
 *   - We do not nest-track open dialogs. If one drawer opens another,
 *     each useFocusTrap handles its own container independently; the
 *     outer one's keydown listener stops firing because the inner
 *     container becomes the active focus root and Tab events bubble
 *     from the inner first.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
  'audio[controls]',
  'video[controls]',
  'details > summary:first-of-type',
].join(',');

export interface UseFocusTrapOptions {
  /** Whether the dialog is currently open (the trap is only armed when true). */
  open: boolean;
  /** Optional explicit element to focus on open — defaults to the first focusable in the container. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Optional Escape handler. If omitted, Escape is not intercepted —
   * callers that don't want Esc-to-close (e.g. a long form mid-submit)
   * simply leave this off.
   */
  onEscape?: () => void;
  /**
   * Whether to restore focus to the trigger element on close. Defaults
   * to true. Set false for the rare case where the trigger has been
   * unmounted by the same action that closed the dialog (e.g. dialog
   * opened from a row that was just deleted).
   */
  restoreFocus?: boolean;
}

/**
 * Hook signature:
 *
 *   const dialogRef = useRef<HTMLDivElement | null>(null);
 *   useFocusTrap(dialogRef, { open, onEscape: onClose });
 *   return <div ref={dialogRef} role="dialog" aria-modal="true">...</div>
 *
 * The container ref must point at the outermost element of the dialog
 * (the same node carrying `role="dialog"`), so that the focusable
 * query reaches every interactive descendant — header close button,
 * form fields, footer actions.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  options: UseFocusTrapOptions,
): void {
  const { open, initialFocusRef, onEscape, restoreFocus = true } = options;
  // Track the element that opened the dialog so we can restore focus on
  // close. We use a ref (not state) because reassigning shouldn't cause
  // a re-render of the dialog itself.
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;

    const container = containerRef.current;
    if (!container) return;

    // 1) Remember the trigger so we can put focus back on close.
    triggerRef.current = document.activeElement;

    // 2) Move focus into the dialog.
    //
    // Defer one tick so children with their own mount-time focus calls
    // (e.g. autofocused inputs inside react-hook-form Controllers) don't
    // race us. 0ms is enough for React commit + microtask drain.
    const initialFocusTimer = window.setTimeout(() => {
      const explicit = initialFocusRef?.current;
      if (explicit) {
        explicit.focus();
        return;
      }
      const first = getFocusableElements(container)[0];
      if (first) {
        first.focus();
      } else {
        // Container has no focusable children — focus the container
        // itself so screen readers announce the dialog landing and
        // arrow-key nav doesn't escape immediately. We add tabIndex=-1
        // so it's programmatically focusable without entering the tab
        // order.
        if (!container.hasAttribute('tabindex')) {
          container.setAttribute('tabindex', '-1');
        }
        container.focus();
      }
    }, 0);

    // 3) Intercept Tab / Shift+Tab for wrap, and Escape if onEscape is set.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        // Nothing to tab to — pin focus on the container itself.
        e.preventDefault();
        container.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;

      // If focus has escaped the container (e.g. clicked on body) put
      // it back on the first focusable so Tab cycling resumes inside.
      if (!active || !container.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
        return;
      }
      // Otherwise let the browser handle normal Tab navigation.
    };

    // Listen on the container, not window, so the trap only fires while
    // focus is inside the dialog — nested dialogs each get their own
    // contained listener without stepping on the parent.
    container.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(initialFocusTimer);
      container.removeEventListener('keydown', onKeyDown);
      if (restoreFocus) {
        const trigger = triggerRef.current as HTMLElement | null;
        if (trigger && document.body.contains(trigger)) {
          // Defer to next tick so React has flushed the dialog unmount
          // before we restore focus — otherwise the browser may briefly
          // park focus on <body> and skip the restore.
          window.setTimeout(() => trigger.focus(), 0);
        }
      }
    };
    // We intentionally don't depend on initialFocusRef / onEscape /
    // restoreFocus — they're read lazily inside the handler so callers
    // can pass inline closures without re-arming the trap every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, containerRef]);
}

/**
 * Read every focusable descendant of `root` in document order, skipping
 * elements that are visually hidden (display:none / visibility:hidden)
 * — we don't want Tab to land on an off-screen field that no sighted
 * user can see.
 *
 * Exported for tests; not part of the public API beyond that.
 */
export function getFocusableElements(root: HTMLElement): HTMLElement[] {
  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return candidates.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // `el.offsetParent === null` catches display:none AND fixed-position
    // detached subtrees in some browsers. JSDOM doesn't compute layout
    // so we accept that test environments will treat every element as
    // visible — that's fine, the trap logic is what we're testing.
    if (typeof el.offsetParent !== 'undefined' && el.offsetParent === null) {
      // Belt-and-braces: still allow body-rooted fixed elements (modal
      // panels) — they often report offsetParent=null but are visible.
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
    }
    return true;
  });
}
